// Nhóm 1 — Tổng chi phí marketing: chi bao nhiêu, cho mạng quảng cáo nào, chiến dịch nào,
// và thu về bao nhiêu khách / doanh thu.
import { useMemo, useState } from 'react';
import { Input, Segmented, Select, Switch, Table, Tag, Tooltip } from 'antd';
import { Link } from 'react-router-dom';
import { ArrowRight, FlaskConical, Megaphone, Pencil, Search } from 'lucide-react';
import { CartesianGrid, Cell, Pie, PieChart, ReferenceLine, ResponsiveContainer, Scatter, ScatterChart, Tooltip as ChartTooltip, XAxis, YAxis, ZAxis } from 'recharts';
import {
  CAMPAIGN_RULES, MKT, campaignRows, costChain, fmtFull, fmtMoney, fmtRate, networkRows, ratio,
  type CampaignRow, type Insight, type NetworkRow, type Range, type Snapshot, type Totals,
} from './insights';
import { useApprovedBudget } from './budgetStore';
import { BudgetModal } from './BudgetModal';
import { BarList, Delta, GroupAlerts, GroupHeader, KpiGrid, KpiTile, PALETTE, Panel, SplitBar, SubTitle } from './ui';

const num = (v: number) => Math.round(v).toLocaleString('vi-VN');
const roasText = (v: number | null) => (v === null ? '—' : `${v.toFixed(v < 1 ? 2 : 1).replace('.', ',')}x`);
const per = (a: number, b: number) => (a > 0 && b > 0 ? a / b : null);
const fmtN1 = (v: number | null) => (v === null ? '—' : v >= 10 ? Math.round(v).toLocaleString('vi-VN') : v.toFixed(1).replace('.', ','));

const NETWORK_COLOR: Record<string, string> = { Facebook: '#1877F2', TikTok: '#111827', Zalo: '#06B6D4', Google: '#EA4335', YouTube: '#FF0000', 'Chưa phân loại': '#CBD5E1' };
export const networkColor = (name: string, i: number) => NETWORK_COLOR[name] || PALETTE[i % PALETTE.length];

type BudgetLevel = 'none' | 'ok' | 'near' | 'over';
const BUDGET_TONE: Record<BudgetLevel, string> = { none: '', ok: '', near: 'text-amber-600', over: 'text-red-500' };
const BUDGET_BAR: Record<BudgetLevel, string> = { none: '#CBD5E1', ok: MKT.color, near: '#EF9F27', over: '#E24B4A' };

/** Thanh % ngân sách đã tiêu + số còn lại / vượt */
const BudgetBar = ({ pct, level, text }: { pct: number; level: BudgetLevel; text: string }) => (
  <span className="flex items-center gap-2">
    <span className="h-1.5 flex-1 overflow-hidden rounded-full bg-slate-100">
      <span className="block h-full rounded-full" style={{ width: `${Math.min(100, pct)}%`, background: BUDGET_BAR[level] }} />
    </span>
    <span className={`shrink-0 ${level === 'over' ? 'text-red-500' : ''}`}>{text}</span>
  </span>
);

/** Công tắc dữ liệu mẫu — chỉ có ở dev / localhost khi server chưa có chiến dịch nào */
export interface SampleToggle { on: boolean; active: boolean; onToggle: (on: boolean) => void }

export interface MarketingGroupProps {
  cur: Snapshot; prev: Snapshot; c: Totals; p: Totals; alerts: Insight[]; prevLabel: string; range: Range; branchNote?: boolean; sample?: SampleToggle;
}

export function MarketingGroup({ cur, prev, c, p, alerts, prevLabel, range, branchNote, sample }: MarketingGroupProps) {
  const networks = useMemo(() => networkRows(cur.mkt), [cur.mkt]);
  const prevNetworks = useMemo(() => networkRows(prev.mkt), [prev.mkt]);
  const camps = useMemo(() => campaignRows(cur.mkt), [cur.mkt]);
  const paid = networks.filter(n => n.spent > 0 && n.name !== 'Chưa phân loại');
  const budgeted = camps.filter(x => x.budget > 0).length;
  // ưu tiên ngân sách quản lý đã duyệt (nhập tay theo tháng); chưa nhập thì dùng tổng ngân sách các chiến dịch
  const approved = useApprovedBudget(range.start, range.end, range.prevStart, range.prevEnd);
  const [budgetOpen, setBudgetOpen] = useState(false);
  const budget = approved.cur?.total || c.budget;
  const prevBudget = approved.prev?.total || p.budget;
  const usedPct = budget > 0 ? (c.spend / budget) * 100 : null;
  const prevUsedPct = prevBudget > 0 ? (p.spend / prevBudget) * 100 : null;
  const budgetLevel: BudgetLevel = usedPct === null ? 'none' : usedPct > 100 ? 'over' : usedPct >= 90 ? 'near' : 'ok';
  const hasCampRevenue = camps.some(x => x.revenue > 0);

  return (
    <>
      <GroupHeader
        index={1} title="Tổng chi phí marketing" color={MKT.color} soft={MKT.soft}
        subtitle={paid.length ? `Đang chi cho ${paid.length} mạng: ${paid.map(x => x.name).join(', ')}` : 'Chi phí quảng cáo theo mạng, chiến dịch và số khách thu về'}
        extra={<>
          {sample && <span className="flex items-center gap-1.5 text-xs text-slate-500"><Switch size="small" checked={sample.on} onChange={sample.onToggle} />Dữ liệu mẫu</span>}
          <Link to="/marketing-campaign-report" className="flex items-center gap-1 text-xs font-medium text-[#357f96] hover:underline">Báo cáo chiến dịch <ArrowRight size={13} /></Link>
        </>}
      />
      {sample?.active && (
        <div className="mb-3 flex flex-wrap items-center gap-x-3 gap-y-1 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
          <FlaskConical size={14} className="shrink-0" />
          <span className="min-w-0 flex-1"><b>Đang xem dữ liệu mẫu.</b> Server chưa có chiến dịch quảng cáo nào — số liệu chi phí, chiến dịch và ngân sách bên dưới (và chi phí ở Hành trình khách hàng) là giả lập để xem giao diện. Tự tắt khi có chiến dịch thật.</span>
          <button type="button" onClick={() => sample.onToggle(false)} className="shrink-0 font-medium text-amber-900 underline">Tắt dữ liệu mẫu</button>
        </div>
      )}
      {branchNote && <div className="mb-3 rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-700">Chi phí quảng cáo chưa lọc được theo chi nhánh — đang tính toàn hệ thống.</div>}
      <GroupAlerts items={alerts} />

      {!cur.mkt ? <Panel title="Chi phí quảng cáo" className="mb-4"><div className="py-8 text-center text-sm text-slate-400">Chưa có dữ liệu chi phí quảng cáo trong kỳ</div></Panel> : (
        <>
          {/* chi phí / tin nhắn, / khách mới và ROAS đã có ở "Chi 1 đồng thu về bao nhiêu" → ở đây là ngân sách */}
          <KpiGrid cols={5}>
            <KpiTile label="Tổng chi phí" value={<Tooltip title={fmtFull(c.spend)}>{fmtMoney(c.spend)}</Tooltip>} delta={<Delta cur={c.spend} prev={p.spend} neutral compact />}
              sub="tổng các mạng quảng cáo" />
            <div className="relative">
              <KpiTile
                label="Ngân sách đã duyệt" value={budget ? <Tooltip title={fmtFull(budget)}>{fmtMoney(budget)}</Tooltip> : '—'}
                delta={budget ? <Delta cur={budget} prev={prevBudget || null} neutral compact /> : undefined}
                sub={approved.cur
                  ? <span className={approved.cur.complete ? '' : 'text-amber-600'}>{approved.cur.complete ? 'quản lý đã duyệt' : 'thiếu ngân sách một số tháng'}</span>
                  : c.budget ? `tổng ngân sách ${budgeted} chiến dịch` : 'chưa nhập ngân sách'}
                info="Ngân sách quản lý duyệt theo tháng (bấm biểu tượng bút để nhập). Chưa nhập thì lấy tổng ngân sách các chiến dịch. Xem một phần tháng thì chia theo số ngày."
              />
              <Tooltip title="Nhập ngân sách đã duyệt">
                <button type="button" aria-label="Nhập ngân sách đã duyệt" onClick={() => setBudgetOpen(true)} className="absolute right-2 top-2 rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-[#357f96]">
                  <Pencil size={13} />
                </button>
              </Tooltip>
            </div>
            <KpiTile label="Ngân sách đã tiêu" value={<span className={BUDGET_TONE[budgetLevel]}>{fmtRate(usedPct)}</span>} delta={usedPct !== null ? <Delta cur={usedPct} prev={prevUsedPct} kind="points" neutral compact /> : undefined}
              sub={usedPct === null ? 'chưa có ngân sách để so' : <BudgetBar pct={usedPct} level={budgetLevel} text={c.spend > budget ? `vượt ${fmtMoney(c.spend - budget)}` : `còn ${fmtMoney(budget - c.spend)}`} />}
              info="Tổng chi phí / ngân sách đã duyệt. Cam khi đã tiêu từ 90%, đỏ khi vượt ngân sách." />
            <KpiTile label="Mạng quảng cáo" value={paid.length} sub={paid.map(x => x.name).join(' · ') || 'chưa phát sinh chi phí'} />
            <div className="col-span-2 md:col-span-1">
              <KpiTile label="Chiến dịch đang chạy" value={`${camps.filter(x => x.active).length}/${camps.length}`} sub={`${camps.filter(x => x.spent > 0).length} chiến dịch có chi phí`} />
            </div>
          </KpiGrid>

          <div className="mb-4 grid grid-cols-1 gap-4 xl:grid-cols-3">
            <NetworkPanel rows={networks} prevRows={prevNetworks} total={c.spend} showRevenue={hasCampRevenue} className="xl:col-span-2" />
            <Panel title="Nguồn khách hàng" info="Lead = người nhắn tin từ quảng cáo (theo báo cáo chiến dịch). Khách hàng mới = khách được Trực page tạo hồ sơ, gồm cả khách tự nhiên.">
              <div className="flex items-baseline gap-3">
                <span className="text-3xl font-bold text-slate-800">{num(c.leads)}</span>
                <Delta cur={c.leads} prev={p.leads} />
                <span className="text-xs text-slate-400">khách mới</span>
              </div>
              <SubTitle>Tin nhắn</SubTitle>
              <SplitBar parts={[{ label: 'Quảng cáo', value: c.adMessages, color: MKT.color }, { label: 'Tự nhiên', value: c.organicMessages, color: '#94a3b8' }]} />
              <div className="mt-3 space-y-1 rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-600">
                <div className="flex justify-between"><span>Lead từ quảng cáo</span><b className="text-slate-800">{num(c.adLeads)}</b></div>
                <div className="flex justify-between"><span>Chi phí / lead</span><b className="text-slate-800">{per(c.spend, c.adLeads) ? fmtMoney(per(c.spend, c.adLeads)!) : '—'}</b></div>
                <div className="flex justify-between"><span>Khách mới / lead quảng cáo</span><b className="text-slate-800">{fmtRate(ratio(c.leads, c.adLeads))}</b></div>
              </div>
            </Panel>
          </div>

          <BudgetModal open={budgetOpen} month={range.end} onClose={() => setBudgetOpen(false)} />
          <CostChainPanel c={c} p={p} prevLabel={prevLabel} />
          <CampaignPanel cur={cur} onSample={sample && !sample.on ? () => sample.onToggle(true) : undefined} />
        </>
      )}
    </>
  );
}

/** Chi phí theo từng mạng quảng cáo: tỷ trọng + hiệu quả */
function NetworkPanel({ rows, prevRows, total, className, showRevenue }: { rows: NetworkRow[]; prevRows: NetworkRow[]; total: number; className?: string; showRevenue: boolean }) {
  const pie = rows.filter(r => r.spent > 0);
  const prevOf = (name: string) => prevRows.find(r => r.name === name);
  return (
    <Panel title="Chi phí theo mạng quảng cáo" className={className} info="Gộp các chiến dịch theo kênh. 'Chưa phân loại' là phần tổng đã chi không khớp với chiến dịch nào.">
      {!rows.length ? <div className="py-8 text-center text-sm text-slate-400">Chưa có dữ liệu</div> : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-[200px_1fr]">
          <div className="relative mx-auto h-[200px] w-[200px]">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={pie} dataKey="spent" nameKey="name" innerRadius={62} outerRadius={92} paddingAngle={pie.length > 1 ? 2 : 0} stroke="none">
                  {pie.map((r, i) => <Cell key={r.name} fill={networkColor(r.name, i)} />)}
                </Pie>
                <ChartTooltip formatter={(v, n) => [fmtFull(Number(v)), n]} />
              </PieChart>
            </ResponsiveContainer>
            <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
              <span className="text-xs text-slate-400">Tổng chi</span>
              <span className="text-lg font-bold text-slate-800">{fmtMoney(total)}</span>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[560px] text-[13px]">
              <thead>
                <tr className="border-b border-slate-100 text-left text-xs text-slate-400">
                  <th className="py-2 font-medium">Mạng</th>
                  <th className="py-2 text-right font-medium">Đã chi</th>
                  <th className="py-2 text-right font-medium">Chiến dịch</th>
                  <th className="py-2 text-right font-medium">Tin nhắn</th>
                  <th className="py-2 text-right font-medium">Lead</th>
                  <th className="py-2 text-right font-medium">Chi phí/lead</th>
                  {showRevenue && <th className="py-2 text-right font-medium">ROAS</th>}
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => {
                  const pv = prevOf(r.name);
                  return (
                    <tr key={r.name} className="border-b border-slate-50">
                      <td className="py-2">
                        <span className="flex items-center gap-2 font-medium text-slate-700">
                          <span className="h-2.5 w-2.5 rounded-sm" style={{ background: networkColor(r.name, i) }} />{r.name}
                          {!r.integrated && <Tooltip title={r.note || 'Chưa kết nối tài khoản quảng cáo — số liệu có thể thiếu'}><Tag className="m-0 text-[11px]">Chưa kết nối</Tag></Tooltip>}
                        </span>
                      </td>
                      <td className="py-2 text-right">
                        <div className="font-semibold text-slate-800" title={fmtFull(r.spent)}>{fmtMoney(r.spent)}</div>
                        <div className="text-[11px] text-slate-400">{fmtRate(r.share)} {pv && <Delta cur={r.spent} prev={pv.spent} neutral compact />}</div>
                      </td>
                      <td className="py-2 text-right">{r.campaigns ? `${r.activeCampaigns}/${r.campaigns}` : '—'}</td>
                      <td className="py-2 text-right">{r.messages ? num(r.messages) : '—'}</td>
                      <td className="py-2 text-right font-medium">{num(r.leads)}</td>
                      <td className="py-2 text-right">{r.cpl ? fmtMoney(r.cpl) : '—'}</td>
                      {showRevenue && <td className={`py-2 text-right ${r.roas !== null && r.roas >= 1 ? 'font-semibold text-emerald-600' : 'text-slate-600'}`}>{roasText(r.roas)}</td>}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </Panel>
  );
}

/** Chuỗi chi phí: mỗi bước của hành trình tốn bao nhiêu tiền quảng cáo, 1 triệu mang về bao nhiêu */
function CostChainPanel({ c, p, prevLabel }: { c: Totals; p: Totals; prevLabel: string }) {
  const chain = costChain(c, p);
  const roas = c.spend > 0 ? c.revenue / c.spend : null;
  const by = (k: string) => chain.find(s => s.key === k)?.per1m ?? null;
  // bước có số lượng 0 thì không có đơn giá → nói "chưa có" thay vì chia cho 0
  const people = chain.filter(x => !x.money && c.spend > 0);
  const many = people.filter(x => x.per1m !== null && x.per1m >= 1);
  const few = people.filter(x => x.unitCost !== null && x.per1m !== null && x.per1m < 1);
  const none = people.filter(x => x.unitCost === null);
  return (
    <Panel
      className="mb-4" title="Chi 1 đồng thu về bao nhiêu"
      info="Chi phí mỗi bước = tổng chi phí quảng cáo / số lượng ở bước đó. Khách đến, khách chốt, doanh thu là của cả công ty nên đây là chi phí bình quân."
    >
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 xl:grid-cols-7">
        {/* số lượng từng bước đã có ở "Hành trình khách hàng" → ở đây số lớn là chi phí / người và ROAS */}
        {chain.map(s => {
          const spend = s.key === 'spend';
          const revenue = s.key === 'revenue';
          return (
            <div key={s.key} className="rounded-lg p-2.5" style={{ background: s.money ? (spend ? MKT.soft : '#E6F5EF') : '#F8FAFC' }}>
              <div className="truncate text-xs text-slate-500" title={s.label}>{spend ? 'Chi phí quảng cáo' : revenue ? 'Doanh thu / chi phí (ROAS)' : `Chi phí / ${s.label.toLowerCase()}`}</div>
              <div className="flex items-baseline justify-between gap-1">
                <span className="text-lg font-bold text-slate-800">{spend ? fmtMoney(s.value) : revenue ? roasText(roas) : s.unitCost !== null ? fmtMoney(s.unitCost) : '—'}</span>
                {spend ? <Delta cur={c.spend} prev={p.spend} neutral compact />
                  : revenue ? <Delta cur={roas} prev={p.spend > 0 ? p.revenue / p.spend : null} compact />
                    : <Delta cur={s.unitCost} prev={s.prevUnitCost} goodWhenUp={false} compact />}
              </div>
              <div className="truncate text-xs text-slate-400">{spend ? 'đầu vào' : revenue ? `doanh thu ${fmtMoney(s.value)}` : `trên ${num(s.value)} người`}</div>
            </div>
          );
        })}
      </div>
      <div className="mt-3 rounded-lg bg-slate-50 px-3 py-2.5 text-[13px] text-slate-600">
        Cứ <b className="text-slate-800">1 triệu</b> chi quảng cáo mang về{' '}
        {many.map(x => `${fmtN1(x.per1m)} ${x.label.toLowerCase()}`).join(', ') || 'chưa tới 1 người ở mọi bước'}
        {few.length > 0 && <> · để có <b className="text-slate-800">1 {few.map(x => `${x.label.toLowerCase()} cần ${fmtMoney(x.unitCost!)}`).join(', 1 ')}</b></>}
        {none.length > 0 && <> · chưa có {none.map(x => x.label.toLowerCase()).join(', ')}</>}
        {by('revenue') !== null && <> · doanh thu thu về <b className="text-emerald-600">{fmtMoney(by('revenue')!)}</b>/1 triệu</>}
        <span className="text-slate-400"> · {prevLabel}: </span>
        <Delta cur={roas} prev={p.spend > 0 ? p.revenue / p.spend : null} />
      </div>
    </Panel>
  );
}

type StatusFilter = 'all' | 'active' | 'paused';

/** Hiệu quả từng chiến dịch: biểu đồ chi phí × số khách + bảng chi tiết */
function CampaignPanel({ cur, onSample }: { cur: Snapshot; onSample?: () => void }) {
  const all = useMemo(() => campaignRows(cur.mkt), [cur.mkt]);
  const [status, setStatus] = useState<StatusFilter>('all');
  const [channel, setChannel] = useState<string | undefined>();
  const [q, setQ] = useState('');
  const channels = Array.from(new Set(all.map(r => r.channel)));

  const rows = useMemo(() => {
    const k = q.trim().toLowerCase();
    return all.filter(r => (status === 'all' || (status === 'active') === r.active) && (!channel || r.channel === channel) && (!k || r.name.toLowerCase().includes(k)));
  }, [all, status, channel, q]);

  const sum = rows.reduce((s, r) => ({ spent: s.spent + r.spent, messages: s.messages + r.messages, leads: s.leads + r.leads, revenue: s.revenue + r.revenue, budget: s.budget + r.budget }), { spent: 0, messages: 0, leads: 0, revenue: 0, budget: 0 });
  const avgCpl = per(sum.spent, sum.leads);
  const spentRows = rows.filter(r => r.spent > 0);
  const maxX = Math.max(1, ...spentRows.map(r => r.spent / 1e6));
  const hasRevenue = all.some(r => r.revenue > 0);
  const tone = (r: CampaignRow) => (r.leads === 0 ? '#E24B4A' : avgCpl && r.cpl! > avgCpl * CAMPAIGN_RULES.expensiveFactor ? '#EF9F27' : '#1D9E75');

  const columns = [
    {
      title: 'Chiến dịch', dataIndex: 'name', fixed: 'left' as const, width: 260,
      render: (v: string, r: CampaignRow) => (
        <div className="min-w-0">
          <div className="flex items-center gap-1.5">
            <Tooltip title={r.active ? 'Đang chạy' : 'Đã dừng'}><span className={`h-2 w-2 shrink-0 rounded-full ${r.active ? 'bg-emerald-500' : 'bg-slate-300'}`} /></Tooltip>
            <span className="truncate font-medium text-slate-700" title={v}>{v}</span>
          </div>
          <div className="mt-0.5 flex gap-1 pl-3.5">
            <Tag className="m-0 text-[11px]" color={r.channel === 'Facebook' ? 'blue' : r.channel === 'TikTok' ? 'default' : 'cyan'}>{r.channel}</Tag>
            {r.leads === 0 && r.spent >= CAMPAIGN_RULES.wasteSpend && <Tag className="m-0 text-[11px]" color="red">Chưa có lead</Tag>}
          </div>
        </div>
      ),
    },
    {
      title: 'Đã chi', dataIndex: 'spent', width: 150, align: 'right' as const, defaultSortOrder: 'descend' as const, sorter: (a: CampaignRow, b: CampaignRow) => a.spent - b.spent,
      render: (v: number, r: CampaignRow) => (
        <div title={fmtFull(v)}>
          <div className="font-semibold text-slate-800">{fmtMoney(v)}</div>
        </div>
      ),
    },
    { title: 'Tin nhắn', dataIndex: 'messages', width: 100, align: 'right' as const, sorter: (a: CampaignRow, b: CampaignRow) => a.messages - b.messages, render: num },
    { title: <Tooltip title="Người nhắn tin từ quảng cáo">Lead</Tooltip>, dataIndex: 'leads', width: 110, align: 'right' as const, sorter: (a: CampaignRow, b: CampaignRow) => a.leads - b.leads, render: (v: number) => <b className={v ? 'text-slate-800' : 'text-red-500'}>{num(v)}</b> },
    { title: <Tooltip title="Tỷ lệ tin nhắn trở thành lead">Nhắn → lead</Tooltip>, dataIndex: 'msgToLead', width: 120, align: 'right' as const, sorter: (a: CampaignRow, b: CampaignRow) => (a.msgToLead ?? -1) - (b.msgToLead ?? -1), render: fmtRate },
    { title: 'Chi phí / tin nhắn', dataIndex: 'cpm', width: 130, align: 'right' as const, sorter: (a: CampaignRow, b: CampaignRow) => (a.cpm ?? Infinity) - (b.cpm ?? Infinity), render: (v: number | null) => (v === null ? '—' : fmtMoney(v)) },
    {
      title: 'Chi phí / lead', dataIndex: 'cpl', width: 130, align: 'right' as const, sorter: (a: CampaignRow, b: CampaignRow) => (a.cpl ?? Infinity) - (b.cpl ?? Infinity),
      render: (v: number | null, r: CampaignRow) => (v === null ? <span className="text-slate-300">—</span> : <span className="font-semibold" style={{ color: tone(r) }}>{fmtMoney(v)}</span>),
    },
    { title: 'Doanh thu', dataIndex: 'revenue', width: 130, align: 'right' as const, sorter: (a: CampaignRow, b: CampaignRow) => a.revenue - b.revenue, render: (v: number) => <span title={fmtFull(v)} className={v ? 'font-medium text-emerald-600' : 'text-slate-300'}>{fmtMoney(v)}</span> },
    {
      title: 'ROAS', dataIndex: 'roas', width: 90, align: 'right' as const, sorter: (a: CampaignRow, b: CampaignRow) => (a.roas ?? -1) - (b.roas ?? -1),
      render: (v: number | null) => (v === null ? '—' : <span className={v >= 1 ? 'font-semibold text-emerald-600' : 'text-red-500'}>{roasText(v)}</span>),
    },
  ].filter(col => hasRevenue || !['revenue', 'roas'].includes(String(col.dataIndex)));

  return (
    <Panel
      className="mb-4" title="Hiệu quả từng chiến dịch"
      info={`Xanh: chi phí/lead dưới ${CAMPAIGN_RULES.expensiveFactor} lần trung bình. Cam: đắt hơn mức đó. Đỏ: đã chi nhưng chưa có lead. Lead = người nhắn tin từ quảng cáo. Cột doanh thu chỉ hiện khi báo cáo chiến dịch có ghi nhận doanh thu.`}
      extra={all.length ? <span className="text-xs text-slate-400">{rows.length}/{all.length} chiến dịch</span> : undefined}
    >
      {/* kỳ không có chiến dịch nào: một thông báo gọn thay cho bộ lọc + 2 biểu đồ + bảng đều trống */}
      {!all.length ? (
        <div className="flex flex-col items-center gap-2 rounded-lg bg-slate-50 px-4 py-8 text-center">
          <Megaphone size={28} className="text-slate-300" />
          <div className="text-sm font-medium text-slate-600">Chưa có chiến dịch quảng cáo nào trong kỳ</div>
          <div className="max-w-md text-xs text-slate-400">Chiến dịch Facebook / TikTok cần được đồng bộ vào hệ thống thì mới có chi phí, tin nhắn và lead để so sánh.</div>
          <div className="mt-1 flex flex-wrap items-center justify-center gap-x-4 gap-y-1">
            <Link to="/advertising-campaign" className="flex items-center gap-1 text-xs font-medium text-[#357f96] hover:underline">Mở Chiến dịch quảng cáo <ArrowRight size={13} /></Link>
            {onSample && <button type="button" onClick={onSample} className="flex items-center gap-1 text-xs font-medium text-amber-700 hover:underline"><FlaskConical size={13} />Xem bằng dữ liệu mẫu</button>}
          </div>
        </div>
      ) : (<>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <Segmented<StatusFilter> value={status} onChange={setStatus} options={[{ value: 'all', label: 'Tất cả' }, { value: 'active', label: 'Đang chạy' }, { value: 'paused', label: 'Đã dừng' }]} />
        <Select allowClear placeholder="Mọi kênh" value={channel} onChange={setChannel} className="w-36" options={channels.map(x => ({ value: x, label: x }))} />
        <Input allowClear prefix={<Search size={14} className="text-slate-400" />} placeholder="Tìm chiến dịch" value={q} onChange={e => setQ(e.target.value)} className="w-full sm:ml-auto sm:w-56" />
      </div>

      <div className="mb-4 grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <SubTitle right={avgCpl ? <span>Đường chéo = chi phí TB <b className="text-slate-700">{fmtMoney(avgCpl)}</b>/lead · trên đường = rẻ hơn TB</span> : undefined}>Chi phí × số lead thu về</SubTitle>
          {spentRows.length ? (
            <div className="h-[280px]">
              <ResponsiveContainer width="100%" height="100%">
                <ScatterChart margin={{ top: 8, right: 16, left: -8, bottom: 8 }}>
                  <CartesianGrid stroke="#eef2f5" />
                  <XAxis type="number" dataKey="x" name="Chi phí" unit=" tr" tickLine={false} axisLine={false} tick={{ fontSize: 11, fill: '#64748b' }} domain={[0, 'auto']} />
                  <YAxis type="number" dataKey="y" name="Lead" allowDecimals={false} tickLine={false} axisLine={false} tick={{ fontSize: 11, fill: '#64748b' }} />
                  <ZAxis type="number" dataKey="z" range={[50, 500]} />
                  {avgCpl && <ReferenceLine segment={[{ x: 0, y: 0 }, { x: maxX, y: (maxX * 1e6) / avgCpl }]} stroke="#94a3b8" strokeDasharray="4 4" ifOverflow="extendDomain" />}
                  <ChartTooltip
                    cursor={{ strokeDasharray: '3 3' }}
                    content={({ payload }) => {
                      const r: CampaignRow | undefined = payload?.[0]?.payload?.row;
                      if (!r) return null;
                      return (
                        <div className="max-w-[260px] rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs shadow">
                          <div className="mb-1 font-semibold text-slate-800">{r.name}</div>
                          <div>Đã chi: <b>{fmtMoney(r.spent)}</b></div>
                          <div>Lead: <b>{num(r.leads)}</b> · {r.cpl ? `${fmtMoney(r.cpl)}/lead` : 'chưa có lead'}</div>
                          {hasRevenue && <div>Doanh thu: <b>{fmtMoney(r.revenue)}</b> · ROAS {roasText(r.roas)}</div>}
                        </div>
                      );
                    }}
                  />
                  <Scatter
                    data={spentRows.map(r => ({ x: +(r.spent / 1e6).toFixed(2), y: r.leads, z: Math.max(1, r.revenue), row: r }))}
                    shape={(props: any) => <circle cx={props.cx} cy={props.cy} r={Math.max(5, Math.sqrt(props.size || 50) / 2)} fill={tone(props.payload.row)} fillOpacity={0.6} stroke={tone(props.payload.row)} />}
                  />
                </ScatterChart>
              </ResponsiveContainer>
            </div>
          ) : <div className="py-16 text-center text-sm text-slate-400">Chưa có chiến dịch nào phát sinh chi phí</div>}
        </div>
        <div>
          <SubTitle>Tiền đã chi đi đâu</SubTitle>
          <BarList
            color={MKT.color}
            items={[...spentRows].sort((a, b) => b.spent - a.spent).slice(0, 6).map(r => ({
              key: r.key, label: r.name, value: r.spent, display: `${fmtMoney(r.spent)} · ${num(r.leads)} lead`, title: `${r.name}: ${fmtFull(r.spent)}, ${num(r.leads)} lead`,
            }))}
          />
          {sum.spent > 0 && (
            <div className="mt-3 rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-600">
              Top 3 chiếm <b>{fmtRate(([...spentRows].sort((a, b) => b.spent - a.spent).slice(0, 3).reduce((s, r) => s + r.spent, 0) / sum.spent) * 100)}</b> chi phí,
              mang về <b>{fmtRate(ratio([...spentRows].sort((a, b) => b.spent - a.spent).slice(0, 3).reduce((s, r) => s + r.leads, 0), sum.leads) ?? 0)}</b> số lead.
            </div>
          )}
        </div>
      </div>

      <Table<CampaignRow>
        size="middle" rowKey="key" columns={columns} dataSource={rows} scroll={{ x: 1240 }}
        pagination={rows.length > 10 ? { pageSize: 10, showSizeChanger: false } : false}
        locale={{ emptyText: 'Không có chiến dịch trong kỳ' }}
        summary={() => rows.length > 1 ? (
          <Table.Summary fixed>
            <Table.Summary.Row className="bg-slate-50 font-semibold">
              <Table.Summary.Cell index={0}>Tổng {rows.length} chiến dịch</Table.Summary.Cell>
              <Table.Summary.Cell index={1} align="right">{fmtMoney(sum.spent)}</Table.Summary.Cell>
              <Table.Summary.Cell index={2} align="right">{num(sum.messages)}</Table.Summary.Cell>
              <Table.Summary.Cell index={3} align="right">{num(sum.leads)}</Table.Summary.Cell>
              <Table.Summary.Cell index={4} align="right">{fmtRate(ratio(sum.leads, sum.messages))}</Table.Summary.Cell>
              <Table.Summary.Cell index={5} align="right">{per(sum.spent, sum.messages) ? fmtMoney(per(sum.spent, sum.messages)!) : '—'}</Table.Summary.Cell>
              <Table.Summary.Cell index={6} align="right">{avgCpl ? fmtMoney(avgCpl) : '—'}</Table.Summary.Cell>
              {hasRevenue && <Table.Summary.Cell index={7} align="right">{fmtMoney(sum.revenue)}</Table.Summary.Cell>}
              {hasRevenue && <Table.Summary.Cell index={8} align="right">{roasText(sum.spent ? sum.revenue / sum.spent : null)}</Table.Summary.Cell>}
            </Table.Summary.Row>
          </Table.Summary>
        ) : null}
      />
      </>)}
    </Panel>
  );
}
